import axios from 'axios';

import { countryCodeMappings } from '../../settings';
import {paymentMethodToStripeTypes, paymentMethodTypeToPaymentData, confirmIntentToStripeMethod} from './settings';

export const StripeMixin = {

    data() {
        return {
            stripe: null,
            clientSecret: null,
            intentStatus: null,
        }
    },

    methods: {
        setUpStripeJsInstance(publishableKey, stripeAccount) {
                this.stripe = Stripe(publishableKey, {stripeAccount, betas: ['acss_debit_beta_1', 'us_bank_account_beta_2']});
        },

        collectUsBankAccountInfo: async function(secret, companyName, email, chargeImmediately){
            let response;
            if (!chargeImmediately) {
                response = await this.stripe.collectUsBankAccountForSetup(secret, {
                    billing_details: {
                        name: companyName,
                        email: email
                    },
                })
            } else {
                response = await this.stripe.collectUsBankAccountForPayment(secret, {
                    billing_details: {
                        name: companyName,
                        email: email
                    },
                })
            }
            return response
        },

        confirmUSBankAccountInfo: async function(secret, chargeImmediately) {
            let response;
            if (!chargeImmediately) {
                response = await this.stripe.confirmUsBankAccountSetup(secret);
            } else {
                response = await this.stripe.confirmUsBankAccountPayment(secret);
            }
            return response
        },

        balanceVerificationHandler: async function(paymentMethodId) {
            try {
                let paymentMethod = await this.findPaymentMethodOnFile(paymentMethodId, true);
                let balanceVerification = false;
                // Handle linked accounts, then microdeposits / bank account
                if (paymentMethod && paymentMethod.data && paymentMethod.data.payment_method_found && paymentMethod.data.payment_method_found.us_bank_account && paymentMethod.data.payment_method_found.us_bank_account.linked_account) {
                    let linked_account = await this.handleLinkedAccountBalance(paymentMethod.data.payment_method_found.us_bank_account.linked_account)
                    balanceVerification = linked_account && linked_account.data && linked_account.data.balance_verification;
                    this.setIsMicro(false);
                } else if (paymentMethod && paymentMethod.data && paymentMethod.data.payment_method_found && paymentMethod.data.payment_method_found.us_bank_account && paymentMethod.data.payment_method_found.us_bank_account.last4) {    
                    this.setLastFour(paymentMethod.data.payment_method_found.us_bank_account.last4);
                    // this.setIsMicro(true);
                    this.setIsMicro(false);
                }
                return { balanceVerification }
            } catch(e) {
                Sentry.captureMessage(e.toString());
                return { error: e };
            }
        },

        handleLinkedAccountBalance: async function(linkedAccountId) {
            let response = await axios({
                method: 'POST',
                url: '/checkout/handle-linked-account-balance',
                headers: {'Content-type': 'application/json'},
                data: {linkedAccountId},
                timeout: 30000,
            });
            return response;
        },

        handleUSBankConfirmation: async function(response){
            return response
        },

        createIntentForInstantVerification: async function(){
            try {
                // Pass in blank values to satisfy params (used for other intent creations)
                // Instant verifiction is used earlier than previous create intents so we do not have these yet
                let response = await this.createIntentHandler('', '');
                this.clientSecret = response.data.client_secret;
                return response
            } catch (e) {
                return e;
            }
        },
        createAndConfirmIntent: async function({firstName, lastName, paymentMethod, chargeImmediately, email, billingInfo, clientSecret, paymentInfoSubmission}) {
            let paymentMethodResponse = await this.determinePaymentMethodToConfirmIntentWith(firstName, lastName, paymentMethod, chargeImmediately, email, billingInfo, paymentInfoSubmission);
            if (!paymentMethodResponse || paymentMethodResponse.error) {
                return paymentMethodResponse;
            }
            const {paymentMethodId, paymentMethodMandate} = paymentMethodResponse;
            
            try {
                let response = await this.createIntentHandler(paymentMethodMandate, paymentMethodId, clientSecret);
                this.clientSecret = response.data.client_secret;
                this.intentStatus = response.data.status;
            } catch (e) {
                return e;
            }

            if (["ACSS"].includes(paymentMethod) && this.intentStatus === 'processing') {
                return {paymentIntent: {payment_method: paymentMethodId}};
            }

            if (chargeImmediately) {
                try {
                    let confirmIntentStripeMethodName = confirmIntentToStripeMethod['payment'][paymentMethodToStripeTypes[paymentMethod]];
                    let payload = {payment_method: paymentMethodId};
                    let confirmIntentHandler = this.stripe[confirmIntentStripeMethodName];
                    return await confirmIntentHandler(this.clientSecret, payload,{skipMandate:false});
                } catch(e) {
                    return e;
                }
            } else {
                try {
                    let confirmIntentHandler = this.stripe[confirmIntentToStripeMethod['setUp'][paymentMethodToStripeTypes[paymentMethod]]];
                    return await confirmIntentHandler(this.clientSecret, {payment_method: paymentMethodId},{skipMandate:false});
                } catch(e) {
                    return e;
                }
            }
        },

        determinePaymentMethodToConfirmIntentWith: async function(firstName, lastName, paymentMethod, chargeImmediately, email, billingInfo, collectedPaymentData) {
            let accountHolderName = firstName + ' ' + lastName;
            let paymentMethodCreated;
            try {
                paymentMethodCreated = await this.createPaymentMethod(paymentMethod, collectedPaymentData, accountHolderName, email, billingInfo)
                
                if (paymentMethodCreated.paymentMethod && paymentMethodCreated.paymentMethod.id) {
                    let findPaymentMethodOnFile = await this.findPaymentMethodOnFile(paymentMethodCreated.paymentMethod.id);
                    
                    let paymentMethodOnFile = findPaymentMethodOnFile.data.payment_method_found;
                    if (paymentMethodOnFile) {
                        return {paymentMethodId: paymentMethodOnFile.id, paymentMethodMandate: paymentMethodOnFile.mandate};
                    }
                    return {paymentMethodId: paymentMethodCreated.paymentMethod.id, paymentMethodMandate: null};
                }
                return paymentMethodCreated;
            } catch(e) {
                return e;
            }
        },

        createPaymentMethod: async function (paymentMethod, collectedPaymentData, accountHolderName, email, billingInfo) {
            let paymentMethodData = {
                type: paymentMethodToStripeTypes[paymentMethod],
                billing_details: {
                    name: accountHolderName,
                    email: email,
                    address: {
                        line1: billingInfo.address1,
                        city: billingInfo.city,
                        country: countryCodeMappings[billingInfo.country],
                        postal_code: billingInfo.zip
                    }
                }
            };
            paymentMethodData[paymentMethodToStripeTypes[paymentMethod]] = paymentMethodTypeToPaymentData[paymentMethodToStripeTypes[paymentMethod]](collectedPaymentData);
            return await this.stripe.createPaymentMethod(paymentMethodData);
        },

        findPaymentMethodOnFile: async function(paymentMethodId, newCustomersAllowed) {
            let response = await axios({
                method: 'POST',
                url: '/checkout/find-payment-method',
                headers: {'Content-type': 'application/json'},
                data: { paymentMethodId, newCustomersAllowed: newCustomersAllowed || false},
                timeout: 30000,
            });
            return response;
        },

        createIntentHandler: async function(mandate, paymentMethodId,clientSecret) {
            let response = await axios({
                method: 'POST',
                url: '/checkout/create-intent',
                headers: {'Content-type': 'application/json'},
                data: {mandate, paymentMethodId, clientSecret},
                timeout: 30000,
            });
            return response;
        },

    },
}
